<template>
  <v-card
    flat
    class="ma-3 pa-6"
  >
    <v-row align="center" class="pl-0 mb-6 pt-3 pr-3">
      <v-card-title>
        Print Textiles
      </v-card-title>
      <v-spacer></v-spacer>
      <v-text-field
        outlined
        dense
        v-model="filter.query"
        placeholder="Search..."
        append-icon="fas fa-search"
        clearable
        clear-icon="fas fa-times"
        single-line
        hide-details
        @keyup="searchKeyUp()"
        @click:clear="searchKeyUp()"
      ></v-text-field>
      <v-btn
        class="ml-4"
        depressed
        color="default"
        @click="importClick"
        :disabled="!user.can('print-textile.import')"
      >
        <v-icon
          small
          left
        >fas fa-upload</v-icon>
      Import
      </v-btn>

      <v-btn
        class="ml-4"
        depressed
        color="secondary"
        @click="newClick"
        :disabled="!user.can('print-textile.create')"
      >
      <v-icon
        small
        left
      >fas fa-folder-plus</v-icon>
      New
      </v-btn>
    </v-row>

    <v-data-table
      :class="{ 'clickable': user.can('print-textile.update') }"
      :headers="headers"
      :items="items"
      :options.sync="options"
      :server-items-length="totalCount"
      :footer-props="{ 'items-per-page-options': [5, 10, 50] }"
      :loading="isSearching"
      @click:row="rowClick"
    >
    
      <template v-slot:item.is_unbranded="{ item }">
        <v-icon :color="item.is_unbranded ? 'secondary' : 'grey'" x-small>fas fa-circle</v-icon>
      </template>

    </v-data-table>


    <v-dialog
      v-model="dialog"
      max-width="700"
      persistent
    >
      <v-card>
        <v-toolbar color="primary" dark flat short class="px-2">
          <v-toolbar-title>Website</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn @click="dialog = false" icon><v-icon>fas fa-times</v-icon></v-btn>
        </v-toolbar>

        <v-card-text class="mt-12">
          <v-form @submit.prevent>

            <v-text-field
              outlined
              class="mb-2"
              v-model="model.sku"
              :error-messages="errors.sku"
              label="Sku"
            ></v-text-field>

            <v-text-field
              outlined
              class="mb-2"
              v-model="model.color_textile"
              :error-messages="errors.color_textile"
              label="Color textile"
            ></v-text-field>

            <v-text-field
              outlined
              class="mb-2"
              v-model="model.textile_sizes"
              :error-messages="errors.textile_sizes"
              label="Textile sizes"
            ></v-text-field>

            <v-text-field
              outlined
              class="mb-2"
              v-model="model.brezo_internal_reference"
              :error-messages="errors.brezo_internal_reference"
              label="Brezo internal reference"
            ></v-text-field>

          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-8">
          <v-btn
            color="error"
            class=""
            large
            outlined
            depressed
            @click="$refs.deleteItemConfirmDialog.show()"
          >Delete</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            class="px-5"
            large
            outlined
            depressed
            @click="dialog = false"
          >Cancel</v-btn>
          <v-btn
            color="primary"
            class="px-5"
            large
            depressed
            @click="saveClick()"
          >Save</v-btn>
        </v-card-actions>

        <ConfirmDialog
          ref="deleteItemConfirmDialog"
          @confirm="deleteItem()"
          title="Confirm"
          message="This item will be deleted. Are you sure?"
          labelConfirm="OK"
          labelCancel="Cancel"
        ></ConfirmDialog>

      </v-card>

    </v-dialog>
    <ImportDialog
      v-model="importDialog"
      @import="showImportResult"
    ></ImportDialog>
    <ContentDialog
      v-model="importResultDialog"
      width="800"
      title="Import Result"
    >
      <template #content>
        <div class="d-flex justify-center">
          <v-chip
            color="primary"
            text-color="white"
          >Total: {{ importResult.total }}</v-chip>
          <v-chip
            class="ml-4"
            color="success"
            text-color="white"
          >Done: {{ importResult.done }}</v-chip>
          <v-chip
            class="ml-4"
            color="warning"
            text-color="white"
          >Skipped: {{ importResult.skipped }}</v-chip>
          <v-chip
            class="ml-4"
            color="error"
            text-color="white"
          >Invalid: {{ importResult.invalid?.length }}</v-chip>
        </div>
        <v-simple-table
          v-if="importResult.invalid?.length"
          class="mt-4"
        >
          <thead>
            <tr>
              <th>Print sku</th>
              <th>Color textile</th>
              <th>Textile sizes</th>
              <th>Brezo sku</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in importResult.invalid" :key="index">
              <td>{{ item.sku }}</td>
              <td>{{ item.color_textile }}</td>
              <td>{{ item.textile_sizes }}</td>
              <td><span class="red--text">{{ item.brezo_sku }}</span></td>
            </tr>
          </tbody>
        </v-simple-table>
      </template>
    </ContentDialog>

  </v-card>

  
</template>

<script>
import { inject, ref } from 'vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import ImportDialog from '@/components/print-textile/ImportDialog.vue'
import ContentDialog from '@/components/ContentDialog.vue'

export default {
  components: {
    ConfirmDialog,
    ImportDialog,
    ContentDialog,
  },
  setup() {
    const api = inject('api')
    const loader = inject('loader')
    const { user } = api

    const itemCollection = api.collection('print-textiles', {
      params: {
        expand: 'product_variant',
      },
      filter: {
        query: 'query:sku,color_textile,textile_sizes,brezo_internal_reference',
      },
      store: true,
    })

    const { items, filter, totalCount, options, isSearching } = itemCollection

    const productVariantCollection = api.collection('product-variants')

    const { items: productVariants } = productVariantCollection.all()


    const headers = [
      { text: 'Sku', value: 'sku' },
      { text: 'Color textile', value: 'color_textile' },
      { text: 'Textile sizes', value: 'textile_sizes' },
      { text: 'Brezo internal reference', value: 'brezo_internal_reference' },
      { text: 'Product Variant', value: 'product_variant.description' },
    ]

    const dialog = ref(false)
    const importDialog = ref(false)
    const importResultDialog = ref(false)
    const importResult = ref('')

    function showImportResult(result) {
      importResult.value = result
      console.log(importResult.value)
      importResultDialog.value = true
    }

    const itemModel = api.model('print-textiles/:sku,:color_textile,:textile_sizes', {
      params: {
      },
    })
    const { attributes: model, errors, isBusy } = itemModel

    loader.bindRef(isBusy)

    function newClick() {
      itemModel.clear().setAttributes({ status: 9 })
      dialog.value = true
    }

    function importClick() {
      importDialog.value = true
    }

    function rowClick(item) {
      if (user.can('print-textile.update')) {
        itemModel.find(item).then(() => {
          dialog.value = true
        })
      }
    }

    function saveClick() {
      itemModel.save().then(() => {
        itemCollection.search()
        dialog.value = false
      })
    }

    function deleteItem() {
      itemModel.delete().then(() => {
        itemCollection.search()
        dialog.value = false
      })
    }

    const searchKeyUp = itemCollection.debounceSearch(300)

    return {
      user,
      headers,
      items,
      filter,
      searchKeyUp,
      totalCount,
      options,
      isSearching,
      newClick,
      importClick,
      rowClick,
      saveClick,
      deleteItem,
      dialog,
      importDialog,
      importResultDialog,
      importResult,
      showImportResult,
      model,
      errors,
      productVariants,
    }

  },
}
</script>


<style scoped>

:deep(.v-data-table.clickable table tbody tr:not(.v-data-table__empty-wrapper):hover) {
  cursor: pointer;
}

</style>
